import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import Router from "next/router";
import { navigateTo } from "@/lib/browserNavigation";
const STALE_CHUNK_RELOAD_KEY = "stale-chunk-reload";
const STALE_CHUNK_RELOAD_PROBE_KEY = "stale-chunk-reload-probe";
const STALE_CHUNK_RELOAD_WINDOW_MS = 30 * 1000;
export function isStaleChunkError(err: unknown): boolean {
  const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  return /ChunkLoadError|Loading chunk/i.test(message) ||
  // Only known production missing-export signatures get a recovery reload.
  // Safari appends a diagnostic after "is not a function".
  /^TypeError: \(0\s*,\s*[\w$]{1,3}\.(?:cn|isAnvilActiveVariant)\) is not a function\b/.test(message);
}

// A readable sessionStorage can still reject writes (full quota), which would
// leave the fallback blank with no reload to follow.
function readReloadGuard(): string | null {
  const previous = window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
  window.sessionStorage.setItem(STALE_CHUNK_RELOAD_PROBE_KEY, "");
  window.sessionStorage.removeItem(STALE_CHUNK_RELOAD_PROBE_KEY);
  return previous;
}

/**
 * Whether a render error is a stale chunk the boundary will recover from by
 * reloading: one attempt per URL within a short window, so a chunk that keeps
 * failing shows the error splash instead of a reload loop. Without a writable
 * sessionStorage there is no loop guard, so no recovery either. The fallback
 * renders before `onError` runs, so it asks this first to stay blank and
 * unreported while the reload is about to happen.
 */
export function canRecoverFromStaleChunk(error: unknown): boolean {
  if (!isStaleChunkError(error)) return false;
  try {
    const previous = readReloadGuard();
    if (!previous) return true;
    const [previousUrl, at] = previous.split("\n");
    return previousUrl !== window.location.href || Date.now() - Number(at) >= STALE_CHUNK_RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

/**
 * App Router counterpart of `useStaleChunkRecovery`: a lazy chunk that stops
 * resolving after a deploy throws into the top-level error boundary rather
 * than failing a navigation, so the boundary hard-navigates to the current
 * URL.
 */
export function recoverFromStaleChunkRender(error: unknown) {
  if (!canRecoverFromStaleChunk(error)) return;
  const url = window.location.href;
  try {
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, `${url}\n${Date.now()}`);
  } catch {
    return;
  }
  navigateTo(url);
}

/**
 * Recovers from stale-chunk navigation failures (the old deployment's lazy
 * chunks stop resolving after a deploy) by hard-navigating instead of
 * surfacing the error splash. Only the Pages Router reports those as
 * routeChangeError; on the App Router they throw during render, see
 * `recoverFromStaleChunkRender`.
 */
export function useStaleChunkRecovery() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
}
function _temp2() {
  if (!Router.router) {
    return;
  }
  const recoverFromStaleChunk = _temp;
  Router.events.on("routeChangeError", recoverFromStaleChunk);
  return () => {
    Router.events.off("routeChangeError", recoverFromStaleChunk);
  };
}
function _temp(err, url) {
  if (url && isStaleChunkError(err)) {
    navigateTo(url);
  }
}